import { useEffect, useRef, useState } from 'react'
import { Paper, Grid, Tooltip, Typography, Link } from '@mui/material'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import PageTitle from '../common/PageTitle'
import TextInput from '../common/TextInput'
import PhoneNumberInput from '../common/PhoneNumberInput'
import ButtonWithProgress from '../common/ButtonWithProgress'
import Separator from '../common/Separator'

import authService from '../../service/authService'
import intl from '../../helper/intl'
import validate from '../../helper/validate'
import utils from '../../helper/utils'
import useGoogleRecaptcha from '../../hooks/useGoogleRecaptcha'

import { useSelector, useDispatch } from 'react-redux'
import { requestPasswordStart, requestPasswordSuccess, requestPasswordFailure, passwordRequestDataChange } from '../../actions/authAction'
import { openAppSnackbar, closeAppSnackbar } from '../../actions/notificationsAction'

import useStyles from './PasswordRequestPage.styles'
import BasicDialog from '../common/BasicDialog'

const GOOGLE_CAPTCHA_V2_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_V2_KEY || '';
const GOOGLE_CAPTCHA_V3_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_V3_KEY || '';

const SCREEN_TYPE = {
  DEFAULT: "DEFAULT",
  REQUEST_EMAIL: "REQUEST_EMAIL"
}

const PasswordRequestPage = () => {
  const companyData = useSelector((state) => state.auth.companyData)
  const requestPasswordLoading = useSelector((state) => state.auth.requestPasswordLoading)
  const passwordRequestData = useSelector((state) => state.auth.passwordRequestData)
  const urlBasename = useSelector((state) => state.intl.urlBasename)
  const [serverFormId, setServerFormId] = useState();
  const recaptchaV2Ref = useRef(null);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
  const [recaptchaV2DialogIsOpen, setRecaptchaV2DialogIsOpen] = useState(false);
  const [verifyingRecaptchaV2, setVerifyingRecaptchaV2] = useState(false);
  const [screenType, setScreenType] = useState(SCREEN_TYPE.DEFAULT)
  const history = useHistory()
  const dispatch = useDispatch()
  const classes = useStyles()


  const googleRecaptchaLoaded = useGoogleRecaptcha(GOOGLE_CAPTCHA_V3_KEY, !utils.checkPermission('reset_password_recaptcha'));

  useEffect(() => {
    authService.getFormId()
    .then((result) => {
      setServerFormId(result.data || '');
    })
  }, []);

  useEffect(() => {
    document.title = intl.translate('password_request_page__title') + ' | ' + (companyData.portal_title || intl.translate('app_header__title'))

    if (companyData.name === 'Longueuil' && !passwordRequestData.countryCode) {
      onFormDataChange('countryCode', 'CA') // Initialize countryCode = +1 for Longueuil
    }
  }, [companyData])

  const onFormDataChange = (paramName, paramValue) => {
    const newFormData = { ...passwordRequestData }
    newFormData[paramName] = paramValue
    dispatch(passwordRequestDataChange({ data: newFormData }))
  }

  const validateFormFields = () => {
    if (screenType === SCREEN_TYPE.REQUEST_EMAIL) {
      return validate.isValidEmail(passwordRequestData.email)
    }
    const emailIsValid = validate.isValidEmail(passwordRequestData.email)
    const phoneNumberIsValid = validate.isValidPhoneNumber(passwordRequestData.phoneNumber, {
      countryCode: passwordRequestData.countryCode
    })
    return emailIsValid || phoneNumberIsValid
  }

  const onFormSubmit = async (event) => {
    event?.preventDefault();

    let anyNewField = false

    if (!passwordRequestData.email && passwordRequestData.phoneNumber && !passwordRequestData.countryCode) {
      dispatch(openAppSnackbar({ message: intl.translate('general__empty_phone_prefix_error') }));
      return false;
    }

    try {
      if (recaptchaV2Token) {
        setVerifyingRecaptchaV2(true);
      } else {
        dispatch(requestPasswordStart());
      }

      const dataWithFormId = {
        ...passwordRequestData,
        formId: serverFormId,
      };

      if (utils.checkPermission('reset_password_recaptcha')) {
        if (recaptchaV2Token) {
          dataWithFormId.recaptcha_token = recaptchaV2Token;
        } else {
          const recaptchaV3Token = await window.grecaptcha.execute(GOOGLE_CAPTCHA_V3_KEY, { action: 'reset_password' });
          dataWithFormId.recaptcha_token = recaptchaV3Token;
        }
      }

      // Add the list of new fields to the request
      dataWithFormId.newFields = utils.getNewFieldNames(companyData)
      if (dataWithFormId.newFields && Array.isArray(dataWithFormId.newFields)) {
        for (const field of dataWithFormId.newFields) {
          if (dataWithFormId[field]) {
            anyNewField = true
          }
        }
      }

      if (screenType === SCREEN_TYPE.REQUEST_EMAIL) {
        dataWithFormId.email_verification = true
      }

      const requestPasswordResult = await authService.requestPasswordReset(dataWithFormId);

      dispatch(requestPasswordSuccess())

      if (requestPasswordResult?.data?.internalCode === 5607) {
        setScreenType(SCREEN_TYPE.REQUEST_EMAIL)
      } else if (screenType === SCREEN_TYPE.REQUEST_EMAIL) {
        dispatch(
          openAppSnackbar({
            message: intl.translate('password_request_page__email_verification_sent'),
            actionText: intl.translate('password_request_page__confirmation_action'),
            actionFunction: () => {
              dispatch(closeAppSnackbar())
              history.push(urlBasename)
            }
          })
        )
      } else {
        dispatch(
          openAppSnackbar({
            message: intl.translate(passwordRequestData.email ? 'server_error__e5603' : 'server_error__e5602'),
            actionText: intl.translate('password_request_page__confirmation_action'),
            actionFunction: () => {
              dispatch(closeAppSnackbar())
            }
          })
        )
      }

      authService.getFormId()
      .then((result) => {
        setServerFormId(result.data || '');
      })

      if (recaptchaV2DialogIsOpen) {
        closeRecaptchaV2Dialog();
        setVerifyingRecaptchaV2(false);
      }

    } catch (error) {
      const errorCode = error?.response?.data?.internalCode;
      if (errorCode === 9000) {
        openRecaptchaV2Dialog();
      } else {
        const errorMessage = intl.getServerError(error?.response?.data) || intl.translate('password_request_page__error');
        dispatch(requestPasswordFailure({ error: errorMessage }));
        dispatch(openAppSnackbar({ message: errorMessage }));
        if (recaptchaV2DialogIsOpen) {
          closeRecaptchaV2Dialog();
          setVerifyingRecaptchaV2(false);
        }
      }
    }
  }

  const openRecaptchaV2Dialog = () => {
    setRecaptchaV2DialogIsOpen(true);
    setTimeout(() => {
      // We need a small timeout so that the recaptchaV2Ref is not null
      if (googleRecaptchaLoaded && recaptchaV2Ref.current && recaptchaV2Ref.current.children.length === 0) {
        window.grecaptcha.render(recaptchaV2Ref.current, {
          'sitekey': GOOGLE_CAPTCHA_V2_KEY,
          'callback': (token) => setRecaptchaV2Token(token),
          'expired-callback': () => setRecaptchaV2Token(null),
        });
      }
    }, 100);
  }

  const closeRecaptchaV2Dialog = (dispatchRegisterFailure) => {
    if (typeof recaptchaV2Ref.current?.reset === 'function') {
      recaptchaV2Ref.current.reset();
    }
    setRecaptchaV2Token(null);
    setRecaptchaV2DialogIsOpen(false);
    if (dispatchRegisterFailure) {
      dispatch(requestPasswordFailure({ error: intl.translate('password_request_page__error') }));
    }
  }

  return (
    <Paper className="_centered_container_">
      <PageTitle>{intl.translate('password_request_page__title')}</PageTitle>

      <form autoComplete="off" onSubmit={onFormSubmit}>
        <Grid container spacing={2}>
          {screenType === SCREEN_TYPE.REQUEST_EMAIL ? (
            <Grid item xs={12}>
              <Typography className={classes.description} variant="body1">
                {intl.translate('password_request_page__insert_email_description_1')}<br />
                {intl.translate('password_request_page__insert_email_description_2')}
              </Typography>
              <TextInput
                id="password-request-page--email"
                label={intl.translate('general__email_label')}
                disabled={requestPasswordLoading}
                value={passwordRequestData.email}
                onChange={(event) => onFormDataChange('email', event.target.value)}
                isValid={(value) => (value ? validate.isValidEmail(value) : true)}
                errorMessage={intl.translate('general__invalid_email')}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography className={classes.description} variant="body1">
                {intl.translate('password_request_page__description')}
              </Typography>

              {utils.checkPermission('reset_password_email_view') && (
                <Tooltip title={passwordRequestData.phoneNumber ? intl.translate('general__only_phone_or_email_necessary') : ''}>
                  <div>
                    <TextInput
                      id="password-request-page--email"
                      label={intl.translate('general__email_label')}
                      disabled={!!passwordRequestData.phoneNumber || requestPasswordLoading}
                      value={passwordRequestData.email}
                      onChange={(event) => onFormDataChange('email', event.target.value)}
                      isValid={(value) => (value ? validate.isValidEmail(value) : true)}
                      errorMessage={intl.translate('general__invalid_email')}
                    />
                  </div>
                </Tooltip>
              )}
              {utils.checkPermission('reset_password_phone_number_view') && (
                <>
                  <Separator className={classes.separator} text={intl.translate('password_request_page__separator')} />

                  <Tooltip title={passwordRequestData.email ? intl.translate('general__only_phone_or_email_necessary') : ''}>
                    <div>
                      <PhoneNumberInput
                        id="password-request-page--phone-number"
                        label={intl.translate('general__phone_number_label')}
                        disabled={!!passwordRequestData.email || requestPasswordLoading}
                        value={passwordRequestData.phoneNumber}
                        countryCodeValue={passwordRequestData.countryCode}
                        onPhoneNumberChange={(event) => onFormDataChange('phoneNumber', event.target.value)}
                        onCountryCodeChange={(event) => onFormDataChange('countryCode', event.target.value)}
                        isValid={(value) =>
                          value ? validate.isValidPhoneNumber(value, { countryCode: passwordRequestData.countryCode }) : true
                        }
                      />
                    </div>
                  </Tooltip>
                </>
              )}
            </Grid>
          )}

          {utils.displayNewFields(companyData, onFormDataChange)}

          <Grid item xs={12}>
            <div className="_centered_btn_container_">
              <ButtonWithProgress
                type="submit"
                disabled={!validateFormFields()}
                showProgress={requestPasswordLoading}
                variant="contained"
                color={window.app.theme.palette.button ? 'button' : 'primary'}
                size="large"
              >
                {intl.translate('password_request_page__submit_btn')}
              </ButtonWithProgress>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography
              className={classes['link-to-login']}
              align="center"
              variant="body1"
              color={window.app.theme.palette.button ? 'link.main' : 'primary'}
            >
              <Link component={RouterLink} to={urlBasename} color="inherit">
                {intl.translate('password_request_page__link_to_login')}
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </form>
      <BasicDialog
        id="password-request-page--confirm-identity-dialog"
        isOpen={recaptchaV2DialogIsOpen}
        title={intl.translate('register_page__confirm_your_identity_title')}
        actionButtons={[
          {
            id: 'password-request-page--confirm-identity-button',
            content: intl.translate('register_page__confirm_your_identity_button'),
            disabled: !recaptchaV2Token,
            showProgress: verifyingRecaptchaV2,
            onClick: () => { onFormSubmit(); }
          }
        ]}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} alignItems="center">
            <div ref={recaptchaV2Ref} className={classes['google-recaptcha']}></div>
          </Grid>
        </Grid>
      </BasicDialog>
    </Paper>
  )
}

export default PasswordRequestPage
